import type { CSSProperties } from 'react'
import type { FixedAssetListItem } from '@/services/fixedAssets'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { parseMoney } from './fixedAssetsFormat'

export const FIXED_ASSETS_SCHEDULES_REGISTER_PAGE_KEY = 'fixed-assets-schedules-register'

export const SCHEDULES_DEFAULT_WIDTHS: Record<string, number> = {
  name: 180,
  asset_class: 110,
  location: 110,
  in_service: 100,
  method: 100,
  useful_life_years: 72,
  purchase_price: 100,
  salvage_value: 90,
  accum_to_date: 130,
  nbv: 90,
  monthly_run_rate: 120,
  pct_depreciated: 130,
  flags: 160,
}

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export const PINNED_COLUMNS = ['name'] as const

export const SCHEDULES_COLUMN_KEYS = [
  'name',
  'asset_class',
  'location',
  'in_service',
  'method',
  'useful_life_years',
  'purchase_price',
  'salvage_value',
  'accum_to_date',
  'nbv',
  'monthly_run_rate',
  'pct_depreciated',
  'flags',
] as const

export type SchedulesColumnKey = (typeof SCHEDULES_COLUMN_KEYS)[number]

export const SCHEDULES_COLUMN_LABELS: Record<SchedulesColumnKey, string> = {
  name: 'Name',
  asset_class: 'Class',
  location: 'Location',
  in_service: 'In service',
  method: 'Method',
  useful_life_years: 'Life',
  purchase_price: 'Cost',
  salvage_value: 'Salvage',
  accum_to_date: 'Accumulated to date',
  nbv: 'NBV',
  monthly_run_rate: 'Monthly expense',
  pct_depreciated: '% depreciated',
  flags: 'Flags',
}

const SORTABLE_COLUMN_KEYS = new Set<SchedulesColumnKey>(
  SCHEDULES_COLUMN_KEYS.filter((key) => key !== 'flags'),
)

export const SCHEDULES_DEFAULT_SORT = [{ field: 'nbv', direction: 'desc' as const }]

const EXPENSE_TYPE_OPTIONS: FieldOption[] = [
  { value: 'depreciation', label: 'Depreciation' },
  { value: 'amortization', label: 'Amortization' },
]

const STATUS_OPTIONS: FieldOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'disposed', label: 'Disposed' },
]

export const FLAG_FILTER_OPTIONS: FieldOption[] = [
  { value: 'non_depreciable', label: 'Non-depreciable' },
  { value: 'fully_depreciated', label: 'Fully depreciated' },
  { value: 'cost_drift', label: 'Cost drift' },
  { value: 'asset_account_missing', label: 'Asset account missing' },
  { value: 'unmapped', label: 'Unmapped' },
]

const MONEY_COLUMN_KEYS = new Set<SchedulesColumnKey>([
  'purchase_price',
  'salvage_value',
  'accum_to_date',
  'nbv',
  'monthly_run_rate',
  'pct_depreciated',
])

export const SCHEDULES_FIELD_DEFINITIONS: FieldDefinition[] = SCHEDULES_COLUMN_KEYS.map((key) => {
  if (key === 'flags') {
    return {
      key,
      label: SCHEDULES_COLUMN_LABELS[key],
      type: 'text',
      sortable: false,
    }
  }
  if (key === 'in_service') {
    return {
      key,
      label: SCHEDULES_COLUMN_LABELS[key],
      type: 'date',
      sortable: true,
      accessor: (row: unknown) => (row as FixedAssetListItem).dep_start_date,
    }
  }
  if (key === 'method') {
    return {
      key,
      label: SCHEDULES_COLUMN_LABELS[key],
      type: 'text',
      sortable: true,
      accessor: (row: unknown) => (row as FixedAssetListItem).method,
    }
  }
  if (key === 'useful_life_years') {
    return {
      key,
      label: SCHEDULES_COLUMN_LABELS[key],
      type: 'number',
      sortable: true,
      accessor: (row: unknown) => (row as FixedAssetListItem).useful_life_years,
    }
  }
  if (MONEY_COLUMN_KEYS.has(key)) {
    return {
      key,
      label: SCHEDULES_COLUMN_LABELS[key],
      type: 'number',
      sortable: true,
      accessor: (row: unknown) =>
        parseMoney((row as FixedAssetListItem)[key as keyof FixedAssetListItem] as string),
    }
  }
  return {
    key,
    label: SCHEDULES_COLUMN_LABELS[key],
    type: 'text',
    sortable: SORTABLE_COLUMN_KEYS.has(key),
    accessor: (row: unknown) => {
      const value = (row as FixedAssetListItem)[key as keyof FixedAssetListItem]
      return typeof value === 'string' ? value.toLowerCase() : value
    },
  }
})

export function isSchedulesColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as SchedulesColumnKey)
}

export function isSchedulesPinnedColumn(columnKey: string): boolean {
  return PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])
}

export function schedulesPinnedHeaderClass(columnKey: string): string {
  if (!isSchedulesPinnedColumn(columnKey)) return ''
  return 'table-sticky-pinned table-sticky-cell table-locked-edge'
}

export function schedulesPinnedCellClass(columnKey: string): string {
  if (!isSchedulesPinnedColumn(columnKey)) return ''
  return 'table-sticky-pinned table-sticky-cell table-locked-edge'
}

export function buildFixedAssetsSchedulesFilterFields(
  locationOptions: FieldOption[],
  assetClassOptions: FieldOption[],
  methodOptions: FieldOption[],
): FieldDefinition[] {
  return [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as FixedAssetListItem).name ?? '',
    },
    {
      key: 'asset_class',
      label: 'Class',
      type: 'multiSelect',
      filterable: true,
      options: assetClassOptions,
      accessor: (row) => (row as FixedAssetListItem).asset_class,
    },
    {
      key: 'location',
      label: 'Location',
      type: 'multiSelect',
      filterable: true,
      options: locationOptions,
      accessor: (row) => (row as FixedAssetListItem).location,
    },
    {
      key: 'expense_type',
      label: 'Expense type',
      type: 'select',
      filterable: true,
      options: EXPENSE_TYPE_OPTIONS,
      accessor: (row) => (row as FixedAssetListItem).expense_type,
    },
    {
      key: 'method',
      label: 'Method',
      type: 'select',
      filterable: true,
      options: methodOptions,
      accessor: (row) => (row as FixedAssetListItem).method,
    },
    {
      key: 'non_depreciable',
      label: 'Non-depreciable',
      type: 'boolean',
      filterable: true,
      accessor: (row) => (row as FixedAssetListItem).non_depreciable,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      filterable: true,
      options: STATUS_OPTIONS,
      accessor: (row) => (row as FixedAssetListItem).status,
    },
    {
      key: 'flag',
      label: 'Flag',
      type: 'multiSelect',
      filterable: true,
      options: FLAG_FILTER_OPTIONS,
      accessor: (row) => (row as FixedAssetListItem).flags.map((flag) => flag.key),
    },
    {
      key: 'purchase_price',
      label: 'Cost',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).purchase_price),
    },
    {
      key: 'salvage_value',
      label: 'Salvage',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).salvage_value),
    },
    {
      key: 'accum_to_date',
      label: 'Accumulated to date',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).accum_to_date),
    },
    {
      key: 'nbv',
      label: 'NBV',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).nbv),
    },
    {
      key: 'monthly_run_rate',
      label: 'Monthly expense',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).monthly_run_rate),
    },
    {
      key: 'pct_depreciated',
      label: '% depreciated',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as FixedAssetListItem).pct_depreciated),
    },
    {
      key: 'useful_life_years',
      label: 'Useful life (years)',
      type: 'number',
      filterable: true,
      accessor: (row) => (row as FixedAssetListItem).useful_life_years,
    },
  ]
}
